import React, { useState, Fragment } from 'react';
import { StudentLayout } from '../layouts/StudentLayout';
import { DownloadIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon, FilterIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from 'lucide-react';
interface Transaction {
  id: string;
  date: Date;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  paymentMethod: string;
  type: 'lesson' | 'subscription' | 'refund';
  teacherName?: string;
  lessonDate?: Date;
  receiptUrl?: string;
}
export const TransactionHistoryPage = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([{
    id: 'tx_1',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    description: 'Piano Lesson with Sarah Johnson',
    amount: 45.0,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    type: 'lesson',
    teacherName: 'Sarah Johnson',
    lessonDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    receiptUrl: '#'
  }, {
    id: 'tx_2',
    date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
    description: 'Monthly Subscription - Premium Plan',
    amount: 19.99,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    type: 'subscription',
    receiptUrl: '#'
  }, {
    id: 'tx_3',
    date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    description: 'Violin Lesson with John Smith',
    amount: 50.0,
    status: 'completed',
    paymentMethod: 'PayPal',
    type: 'lesson',
    teacherName: 'John Smith',
    lessonDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    receiptUrl: '#'
  }, {
    id: 'tx_4',
    date: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000),
    description: 'Cancelled Lesson - Refund',
    amount: -35.0,
    status: 'completed',
    paymentMethod: 'Original Payment Method',
    type: 'refund',
    receiptUrl: '#'
  }, {
    id: 'tx_5',
    date: new Date(Date.now() - 1 * 60 * 60 * 1000),
    description: 'Guitar Lesson with Maria Garcia',
    amount: 40.0,
    status: 'pending',
    paymentMethod: 'Mastercard •••• 5678',
    type: 'lesson',
    teacherName: 'Maria Garcia',
    lessonDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  }, {
    id: 'tx_6',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    description: 'Failed Payment - Insufficient Funds',
    amount: 45.0,
    status: 'failed',
    paymentMethod: 'Mastercard •••• 5678',
    type: 'lesson',
    teacherName: 'Sarah Johnson'
  }]);
  const [filter, setFilter] = useState({
    status: 'all',
    type: 'all',
    searchQuery: '',
    dateRange: 'all'
  });
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  // Toggle transaction details
  const toggleTransaction = (id: string) => {
    if (expandedTransaction === id) {
      setExpandedTransaction(null);
    } else {
      setExpandedTransaction(id);
    }
  };
  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
  };
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({
      ...filter,
      searchQuery: e.target.value
    });
  };
  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Filter by status
    if (filter.status !== 'all' && transaction.status !== filter.status) {
      return false;
    }
    // Filter by type
    if (filter.type !== 'all' && transaction.type !== filter.type) {
      return false;
    }
    // Filter by search query
    if (filter.searchQuery) {
      const query = filter.searchQuery.toLowerCase();
      const matchesDescription = transaction.description.toLowerCase().includes(query);
      const matchesTeacher = transaction.teacherName?.toLowerCase().includes(query);
      if (!matchesDescription && !matchesTeacher) {
        return false;
      }
    }
    // Filter by date range
    if (filter.dateRange !== 'all') {
      const now = new Date();
      const transactionDate = new Date(transaction.date);
      switch (filter.dateRange) {
        case 'today':
          if (transactionDate.toDateString() !== now.toDateString()) {
            return false;
          }
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (transactionDate < weekAgo) {
            return false;
          }
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (transactionDate < monthAgo) {
            return false;
          }
          break;
        case 'year':
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          if (transactionDate < yearAgo) {
            return false;
          }
          break;
      }
    }
    return true;
  });
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400" data-id="element-9847">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-9848" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-900/30 text-amber-400" data-id="element-9849">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-9850" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-400" data-id="element-9851">
            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-9852" />
            Failed
          </span>;
      default:
        return null;
    }
  };
  return <StudentLayout data-id="element-9853">
      <div className="max-w-6xl mx-auto" data-id="element-9854">
        <div className="mb-6" data-id="element-9855">
          <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-9856">
            Transaction History
          </h1>
          <p className="text-gray-300" data-id="element-9857">View and manage your payment history</p>
        </div>
        {/* Filters */}
        <div className="bg-navy-800 rounded-xl p-4 mb-6" data-id="element-9858">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-9859">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-9860">
              <div className="flex items-center" data-id="element-9861">
                <FilterIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-9862" />
                <span className="text-sm text-gray-300 mr-2" data-id="element-9863">Filter:</span>
              </div>
              <div className="flex space-x-2" data-id="element-9864">
                <select name="status" value={filter.status} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-9865">
                  <option value="all" data-id="element-9866">All Statuses</option>
                  <option value="completed" data-id="element-9867">Completed</option>
                  <option value="pending" data-id="element-9868">Pending</option>
                  <option value="failed" data-id="element-9869">Failed</option>
                </select>
                <select name="type" value={filter.type} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-9870">
                  <option value="all" data-id="element-9871">All Types</option>
                  <option value="lesson" data-id="element-9872">Lessons</option>
                  <option value="subscription" data-id="element-9873">Subscriptions</option>
                  <option value="refund" data-id="element-9874">Refunds</option>
                </select>
                <select name="dateRange" value={filter.dateRange} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-9875">
                  <option value="all" data-id="element-9876">All Time</option>
                  <option value="today" data-id="element-9877">Today</option>
                  <option value="week" data-id="element-9878">This Week</option>
                  <option value="month" data-id="element-9879">This Month</option>
                  <option value="year" data-id="element-9880">This Year</option>
                </select>
              </div>
            </div>
            <div className="relative" data-id="element-9881">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9882">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-9883" />
              </div>
              <input type="text" placeholder="Search transactions..." value={filter.searchQuery} onChange={handleSearch} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block w-full pl-10 p-2.5" data-id="element-9884" />
            </div>
          </div>
        </div>
        {/* Transactions list */}
        <div className="bg-navy-800 rounded-xl overflow-hidden mb-8" data-id="element-9885">
          <div className="overflow-x-auto" data-id="element-9886">
            {filteredTransactions.length > 0 ? <table className="min-w-full divide-y divide-navy-700" data-id="element-9887">
                <thead className="bg-navy-700" data-id="element-9888">
                  <tr data-id="element-9889">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9890">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9891">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9892">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9893">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9894">
                      Payment Method
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-9895">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700" data-id="element-9896">
                  {filteredTransactions.map(transaction => <Fragment key={transaction.id} data-id="element-9897">
                      <tr className={`hover:bg-navy-700 transition-colors cursor-pointer ${expandedTransaction === transaction.id ? 'bg-navy-700' : ''}`} onClick={() => toggleTransaction(transaction.id)} data-id="element-9898">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9899">
                          <div className="text-sm text-white" data-id="element-9900">
                            {formatDate(transaction.date)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-9901">
                            {formatTime(transaction.date)}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-9902">
                          <div className="text-sm text-white" data-id="element-9903">
                            {transaction.description}
                          </div>
                          {transaction.teacherName && <div className="text-xs text-gray-400" data-id="element-9904">
                              Teacher: {transaction.teacherName}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9905">
                          <div className={`text-sm font-medium ${transaction.amount < 0 ? 'text-green-400' : transaction.status === 'failed' ? 'text-gray-400' : 'text-white'}`} data-id="element-9906">
                            {formatCurrency(transaction.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9907">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9908">
                          <div className="text-sm text-gray-300" data-id="element-9909">
                            {transaction.paymentMethod}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9910">
                          <button className="text-deep-pink hover:text-burgundy" onClick={e => {
                      e.stopPropagation();
                      // In a real app, this would download the receipt
                      if (transaction.receiptUrl && transaction.status === 'completed') {
                        window.open(transaction.receiptUrl, '_blank');
                      }
                    }} disabled={!transaction.receiptUrl || transaction.status !== 'completed'} data-id="element-9911">
                            {expandedTransaction === transaction.id ? <ChevronUpIcon className="h-5 w-5" data-id="element-9912" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-9913" />}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded details */}
                      {expandedTransaction === transaction.id && <tr className="bg-navy-700" data-id="element-9914">
                          <td colSpan={6} className="px-6 py-4" data-id="element-9915">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9916">
                              <div data-id="element-9917">
                                <h4 className="text-sm font-medium text-white mb-2" data-id="element-9918">
                                  Transaction Details
                                </h4>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-9919">
                                  <dt className="text-gray-400" data-id="element-9920">
                                    Transaction ID:
                                  </dt>
                                  <dd className="text-white" data-id="element-9921">
                                    {transaction.id}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-9922">Date:</dt>
                                  <dd className="text-white" data-id="element-9923">
                                    {formatDate(transaction.date)} at{' '}
                                    {formatTime(transaction.date)}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-9924">Type:</dt>
                                  <dd className="text-white capitalize" data-id="element-9925">
                                    {transaction.type}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-9926">Status:</dt>
                                  <dd className="text-white" data-id="element-9927">
                                    {getStatusBadge(transaction.status)}
                                  </dd>
                                </dl>
                              </div>
                              <div data-id="element-9928">
                                <h4 className="text-sm font-medium text-white mb-2" data-id="element-9929">
                                  Payment Details
                                </h4>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-9930">
                                  <dt className="text-gray-400" data-id="element-9931">Amount:</dt>
                                  <dd className={`font-medium ${transaction.amount < 0 ? 'text-green-400' : transaction.status === 'failed' ? 'text-gray-400' : 'text-white'}`} data-id="element-9932">
                                    {formatCurrency(transaction.amount)}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-9933">
                                    Payment Method:
                                  </dt>
                                  <dd className="text-white" data-id="element-9934">
                                    {transaction.paymentMethod}
                                  </dd>
                                  {transaction.type === 'lesson' && transaction.lessonDate && <>
                                        <dt className="text-gray-400" data-id="element-9935">
                                          Lesson Date:
                                        </dt>
                                        <dd className="text-white" data-id="element-9936">
                                          {formatDate(transaction.lessonDate)}
                                        </dd>
                                        <dt className="text-gray-400" data-id="element-9937">
                                          Teacher:
                                        </dt>
                                        <dd className="text-white" data-id="element-9938">
                                          {transaction.teacherName}
                                        </dd>
                                      </>}
                                </dl>
                                {transaction.receiptUrl && transaction.status === 'completed' && <div className="mt-4" data-id="element-9939">
                                      <a href={transaction.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg text-sm" data-id="element-9940">
                                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-9941" />
                                        Download Receipt
                                      </a>
                                    </div>}
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table> : <div className="text-center py-12" data-id="element-9942">
                <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-navy-700 mb-4" data-id="element-9943">
                  <CreditCardIcon className="h-8 w-8 text-gray-400" data-id="element-9944" />
                </div>
                <p className="text-gray-300 mb-2" data-id="element-9945">No transactions found</p>
                <p className="text-sm text-gray-400" data-id="element-9946">
                  Try adjusting your filters or search criteria
                </p>
              </div>}
          </div>
        </div>
      </div>
    </StudentLayout>;
};